import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

export default function initTimeline() {
  const clamp = (v, a, b) => Math.min(Math.max(v, a), b);

  document.querySelectorAll(".tl").forEach((tl) => {
    const items   = Array.from(tl.querySelectorAll(".tl__item"));
    const trackEl = tl.querySelector(".tl__track");
    if (!items.length || !trackEl) return;

    const DEFAULT_START_INDEX = 0;
    const DEFAULT_START_PCT   = 10;

    const mq = window.matchMedia("(max-width: 1024px)");
    const isVertical = () => mq.matches; // true on mobile
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const startIndex = clamp(
      parseInt(tl.getAttribute("data-start-index") ?? DEFAULT_START_INDEX, 10) || 0,
      0, items.length - 1
    );
    const startPct = clamp(
      parseFloat(tl.getAttribute("data-start-pct") ?? DEFAULT_START_PCT) || 0,
      0, 100
    );

    let current = startIndex;
    let hasBooted = false;

    // Smooth show/hide for the warning card (mobile only)
    const showCard = (card, show) => {
      if (!card) return;

      if (!isVertical() || reduceMotion || !hasBooted) {
        card.classList.toggle("is-visible", show);
        card.setAttribute("aria-hidden", String(!show));
        card.style.pointerEvents = show ? "auto" : "none";
        gsap.killTweensOf(card);
        gsap.set(card, { clearProps: "opacity,visibility,transform" });
        return;
      }

      gsap.killTweensOf(card);

      if (show) {
        card.classList.add("is-visible");
        card.setAttribute("aria-hidden", "false");
        card.style.pointerEvents = "auto";
        gsap.fromTo(
          card,
          { autoAlpha: 0, y: 6 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.28,
            ease: "power2.out",
            overwrite: "auto",
            force3D: true,
            immediateRender: false
          }
        );
      } else {
        gsap.to(card, {
          autoAlpha: 0,
          y: 6,
          duration: 0.22,
          ease: "power2.out",
          overwrite: "auto", 
          force3D: true,
          onComplete: () => {
            card.classList.remove("is-visible");
            card.setAttribute("aria-hidden", "true");
            card.style.pointerEvents = "none";
            gsap.set(card, { clearProps: "opacity,visibility,transform" });
          }
        });
      }
    };

    // compute progress along X (desktop) or Y (mobile)
    const setProgressToDot = (i, useBaselineForFirst = true) => {
      const dot = items[i]?.querySelector(".tl__dot");
      if (!dot) return;

      const trackRect = trackEl.getBoundingClientRect();
      const dotRect   = dot.getBoundingClientRect();
      const isLast    = (i === items.length - 1);

      let pct;

      if (isVertical()) {
        if (isLast) {
          // fill full height when last item is active
          pct = 100;
        } else {
          const dotCenterY = dotRect.top + dotRect.height / 2;
          const progressPx = clamp(dotCenterY - trackRect.top, 0, trackRect.height);
          pct = (progressPx / trackRect.height) * 100;
          if (useBaselineForFirst && i === 0) pct = Math.max(pct, startPct);
        }
      } else {
        if (isLast) {
          // fill full width when last item is active
          pct = 100;
        } else {
          const dotCenterX = dotRect.left + dotRect.width / 2;
          const progressPx = clamp(dotCenterX - trackRect.left, 0, trackRect.width);
          pct = (progressPx / trackRect.width) * 100;
          if (useBaselineForFirst && i === 0) pct = Math.max(pct, startPct);
        }
      }

      pct = clamp(pct, 0, 100);
      tl.style.setProperty("--progress", pct + "%");
    };

    const activate = (i) => {
      if (i === current) return;
      current = i;

      items.forEach((el, idx) => {
        const active = idx === i;
        el.classList.toggle("is-active", active);
        el.classList.toggle("is-complete", idx <= i);
        const hit = el.querySelector(".tl__hit");
        if (hit) hit.setAttribute("aria-expanded", String(active));
        showCard(el.querySelector(".tl__card"), active);
      });

      setProgressToDot(i);
    };

    items.forEach((el, i) => {
      const hit = el.querySelector(".tl__hit") || el;
      const dot = el.querySelector(".tl__dot");

      ["mouseenter", "focus"].forEach((ev) =>
        hit.addEventListener(ev, () => activate(i))
      );
      hit.addEventListener("click", () => activate(i));

      if (dot) {
        dot.setAttribute("tabindex", "0");
        dot.setAttribute("role", "button");
        const labelText = hit?.textContent?.trim() || `Step ${i + 1}`;
        dot.setAttribute("aria-label", `Show step: ${labelText}`);

        ["mouseenter", "focus"].forEach((ev) =>
          dot.addEventListener(ev, () => activate(i))
        );
        dot.addEventListener("click", () => activate(i));
        dot.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            activate(i);
          }
        });
      }
    });

    items.forEach((el, idx) => {
      const active = idx === startIndex;
      el.classList.toggle("is-active", active);
      el.classList.toggle("is-complete", idx <= startIndex);
      const hit = el.querySelector(".tl__hit");
      if (hit) hit.setAttribute("aria-expanded", String(active));
      showCard(el.querySelector(".tl__card"), active);
    });
    setProgressToDot(startIndex, true);

    // Recompute on resize or breakpoint change
    const onResize = () => setProgressToDot(current, false);
    window.addEventListener("resize", onResize, { passive: true });
    mq.addEventListener?.("change", onResize);

    // --- MOBILE SCROLL ANIM + AUTO-SCROLL --------------------------------
    const setupMobileScroll = () => {
      ScrollTrigger.getAll().forEach(st => {
        if (st.vars && st.vars.id && st.vars.id.startsWith('tl-')) st.kill();
      });

      if (!isVertical()) {
        gsap.set(items, { clearProps: "all" });
        tl.removeAttribute('data-mobile-animate');
        hasBooted = true;
        return;
      }

      tl.setAttribute('data-mobile-animate', 'true');

      gsap.set(items, { opacity: 0, y: 14 });

      ScrollTrigger.create({
        id: 'tl-stagger-' + Math.random().toString(36).slice(2),
        trigger: tl,
        start: "top 80%",
        once: true,
        onEnter: () => {
          gsap.to(items, {
            opacity: 1,
            y: 0,
            duration: 0.35,
            ease: "power2.out",
            stagger: 0.12,
            onStart: () => {
              items.forEach((el, idx) => {
                const active = idx === startIndex;
                el.classList.toggle("is-active", active);
                el.classList.toggle("is-complete", idx <= startIndex);
                const hit = el.querySelector(".tl__hit");
                if (hit) hit.setAttribute("aria-expanded", String(active));
                const card = el.querySelector(".tl__card");
                if (card) {
                  card.classList.toggle("is-visible", active);
                  card.setAttribute("aria-hidden", String(!active));
                  card.style.pointerEvents = active ? "auto" : "none";
                  gsap.set(card, { clearProps: "opacity,visibility,transform" });
                }
              });
              setProgressToDot(startIndex, true);
              hasBooted = true;
            }
          });

          items.forEach((el, i) => {
            const dot = el.querySelector('.tl__dot');
            if (!dot) return;
            gsap.fromTo(dot, { scale: 0.9 }, {
              scale: 1, duration: 0.25, ease: "power2.out",
              delay: i * 0.12 + 0.05, overwrite: "auto", force3D: true
            });
          });
        }
      });

      items.forEach((el, i) => {
        ScrollTrigger.create({
          id: `tl-item-${i}`,
          trigger: el,
          start: "top center+=72",
          end:   "bottom center-=72",
          onEnter:     () => activate(i),
          onEnterBack: () => activate(i),
        });
      });

      requestAnimationFrame(() => ScrollTrigger.refresh());
    };

    setupMobileScroll();
    mq.addEventListener?.("change", setupMobileScroll);
  });
}
